import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { Partage } from 'src/app/core/models/finance.model';
import { PaiementService } from 'src/app/core/services/entities/paiement.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { PhoneUtil } from './phone.util';
import { ToastService } from './toast.service';

/** Ce que l'écran appelant fournit à la fiche de partage */
export interface ContenuPartage {
  titre: string;
  destinataireNom?: string;
  telephone?: string;
  email?: string;
  message: string;
  lien?: string;
  nomFichier?: string;
  /** Récupère le fichier (PDF) pour le partage natif et le téléchargement */
  fichier?: () => Observable<Blob>;
  journal: Omit<Partage, 'fermeId' | 'canal'>;
}

/**
 * Fiche de partage unique (facture, reçu, document, rapport, relance) :
 * WhatsApp avec message pré-rempli et lien, partage natif du fichier (mobile), copier le lien,
 * télécharger, e-mail. Chaque envoi est journalisé ; « Marquer comme envoyé » le confirme.
 */
@Component({
  selector: 'bay-share-sheet',
  template: `
    <bay-bottom-sheet [open]="open" [titre]="contenu?.titre || 'Partager'" (closed)="closed.emit()">
      <ng-container *ngIf="contenu">
        <div class="bay-field">
          <label class="bay-label" for="share-tel">Destinataire</label>
          <div class="d-flex gap-2 align-items-center">
            <span *ngIf="contenu.destinataireNom" class="bay-share-nom">{{ contenu.destinataireNom }}</span>
            <input id="share-tel" type="tel" inputmode="tel" autocomplete="tel" class="bay-input" [(ngModel)]="telephone"
              placeholder="Numéro WhatsApp (facultatif)">
          </div>
          <p class="bay-hint" *ngIf="!telephoneValide">Sans numéro, WhatsApp vous laisse choisir le destinataire.</p>
        </div>
        <div class="bay-field">
          <label class="bay-label" for="share-msg">Message</label>
          <textarea id="share-msg" class="bay-input" rows="7" [(ngModel)]="message"></textarea>
        </div>
        <div class="bay-share-actions">
          <button type="button" class="bay-btn bay-btn-whatsapp" (click)="whatsapp()">
            <span class="material-symbols-outlined" aria-hidden="true">chat</span>WhatsApp</button>
          <button *ngIf="contenu.fichier && partageNatif" type="button" class="bay-btn bay-btn-soft" (click)="partagerFichier()" [disabled]="enCours">
            <span class="material-symbols-outlined" aria-hidden="true">ios_share</span>Partager le PDF</button>
          <button *ngIf="contenu.lien" type="button" class="bay-btn bay-btn-soft" (click)="copierLien()">
            <span class="material-symbols-outlined" aria-hidden="true">link</span>{{ copie ? 'Lien copié' : 'Copier le lien' }}</button>
          <button *ngIf="contenu.fichier" type="button" class="bay-btn bay-btn-soft" (click)="telecharger()" [disabled]="enCours">
            <span class="material-symbols-outlined" aria-hidden="true">download</span>Télécharger</button>
          <a class="bay-btn bay-btn-ghost" [href]="mailto" (click)="journaliser('EMAIL')">
            <span class="material-symbols-outlined" aria-hidden="true">mail</span>E-mail</a>
        </div>
        <label *ngIf="dernierEnvoi" class="d-flex align-items-center gap-2 mt-3 fw-semibold small">
          <input type="checkbox" [checked]="confirme" (change)="confirmer()" [disabled]="confirme"> Marquer comme envoyé
        </label>
        <p class="bay-hint mt-2" *ngIf="contenu.lien">Le lien ouvre le document sans compte ; il peut être désactivé depuis la fiche.</p>
      </ng-container>
    </bay-bottom-sheet>
  `,
  styles: [`
    .bay-share-nom { font-weight: 700; white-space: nowrap; }
    .bay-share-actions { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; }
  `]
})
export class ShareSheetComponent implements OnChanges {
  @Input() open = false;
  @Input() contenu: ContenuPartage | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() envoye = new EventEmitter<Partage>();

  telephone = '';
  message = '';
  copie = false;
  enCours = false;
  dernierEnvoi: Partage | null = null;
  confirme = false;
  readonly partageNatif = typeof navigator !== 'undefined' && typeof (navigator as any).canShare === 'function'
    && (() => { try { return (navigator as any).canShare({ files: [new File([''], 'a.pdf', { type: 'application/pdf' })] }); } catch { return false; } })();

  constructor(private paiements: PaiementService, private fermeState: FermeStateService, private toast: ToastService) {}

  ngOnChanges(c: SimpleChanges): void {
    if ((c.open || c.contenu) && this.open && this.contenu) {
      this.telephone = PhoneUtil.affichage(this.contenu.telephone) || '';
      this.message = this.contenu.message;
      this.copie = false;
      this.dernierEnvoi = null;
      this.confirme = false;
    }
  }

  get telephoneValide(): boolean { return !!PhoneUtil.e164(this.telephone); }

  get mailto(): string {
    const sujet = encodeURIComponent(this.contenu?.titre || '');
    return `mailto:${this.contenu?.email || ''}?subject=${sujet}&body=${encodeURIComponent(this.message)}`;
  }

  whatsapp(): void {
    window.open(PhoneUtil.lienWhatsApp(this.telephone, this.message), '_blank', 'noopener');
    this.journaliser('WHATSAPP');
  }

  partagerFichier(): void {
    if (!this.contenu?.fichier) return;
    this.enCours = true;
    this.contenu.fichier().subscribe({
      next: blob => {
        this.enCours = false;
        const file = new File([blob], this.contenu!.nomFichier || 'document.pdf', { type: blob.type || 'application/pdf' });
        (navigator as any).share({ files: [file], title: this.contenu!.titre, text: this.message })
          .then(() => this.journaliser('PARTAGE_NATIF'))
          .catch(() => { /* partage annulé */ });
      },
      error: () => { this.enCours = false; this.toast.erreur('Impossible de préparer le fichier.'); }
    });
  }

  copierLien(): void {
    const lien = this.contenu?.lien;
    if (!lien) return;
    (navigator.clipboard?.writeText(lien) || Promise.reject()).then(
      () => { this.copie = true; this.journaliser('LIEN'); },
      () => { window.prompt('Copiez le lien :', lien); this.journaliser('LIEN'); });
  }

  telecharger(): void {
    if (!this.contenu?.fichier) return;
    this.enCours = true;
    this.contenu.fichier().subscribe({
      next: blob => {
        this.enCours = false;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.contenu!.nomFichier || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.journaliser('TELECHARGEMENT');
      },
      error: () => { this.enCours = false; this.toast.erreur('Téléchargement impossible.'); }
    });
  }

  journaliser(canal: Partage['canal']): void {
    if (!this.contenu) return;
    this.paiements.journaliser({ ...this.contenu.journal, fermeId: this.fermeState.fermeId, canal,
      destinataire: PhoneUtil.e164(this.telephone) || this.contenu.email || undefined } as Partage).subscribe({
      next: p => { this.dernierEnvoi = p; this.envoye.emit(p); },
      error: () => {}
    });
  }

  confirmer(): void {
    if (!this.dernierEnvoi?.id) return;
    this.paiements.confirmerPartage(this.dernierEnvoi.id).subscribe(() => { this.confirme = true; this.toast.succes('Envoi confirmé'); });
  }
}
